/**
 * Gallery fetch + React Query caching for Parsnip.
 * Calls parsnipApi directly for reads — mirrors the pattern in useVideosQuery.ts.
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { parsnipApi } from '@/api/parsnipApi';
import type { MyAnalysesResponse } from '@/api/parsnipApi';

const LIMIT = 12;

// ─── Query keys ──────────────────────────────────────────────────────────────

export const parsnipKeys = {
  all: ['parsnip'] as const,
  analyses: () => [...parsnipKeys.all, 'analyses'] as const,
  allAnalyses: (sortBy: string, status: string, userEmail: string) => [...parsnipKeys.analyses(), 'all', sortBy, status, userEmail] as const,
  users: () => [...parsnipKeys.all, 'users'] as const,
};

// ─── Infinite query ───────────────────────────────────────────────────────────

export const useAllAnalysesInfinite = (sortBy: string, statusFilter: string, userEmailFilter: string) => {
  return useInfiniteQuery({
    queryKey: parsnipKeys.allAnalyses(sortBy, statusFilter, userEmailFilter),
    queryFn: ({ pageParam = 0 }) =>
      parsnipApi.getAllAnalyses(pageParam, LIMIT, sortBy, statusFilter, userEmailFilter || undefined),
    initialPageParam: 0,
    getNextPageParam: (lastPage: MyAnalysesResponse) => {
      if (!lastPage.has_more) return undefined;
      return (lastPage.skip ?? 0) + (lastPage.limit ?? LIMIT);
    },
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 30,
  });
};

// ─── Cache helpers ────────────────────────────────────────────────────────────

const patchAnalysisItem = (
  item: MyAnalysesResponse['analyses'][number],
  videoId: string,
  thumbnailUrl: string
) => {
  if (item.content_video_id === videoId) {
    return { ...item, content_thumbnail_url: thumbnailUrl };
  }
  const idx = item.result_video_ids.indexOf(videoId);
  if (idx !== -1) {
    const newThumbs = [...item.result_thumbnail_urls];
    newThumbs[idx] = thumbnailUrl;
    return { ...item, result_thumbnail_urls: newThumbs };
  }
  return item;
};

const patchPage = (
  page: MyAnalysesResponse,
  videoId: string,
  thumbnailUrl: string
): MyAnalysesResponse => ({
  ...page,
  analyses: page.analyses.map(item => patchAnalysisItem(item, videoId, thumbnailUrl)),
});

export const useParsnipQueryClient = () => {
  const queryClient = useQueryClient();

  const invalidateAnalyses = async () => {
    await queryClient.invalidateQueries({ queryKey: parsnipKeys.analyses() });
  };

  const patchThumbnailInCache = (videoId: string, thumbnailUrl: string) => {
    queryClient.setQueriesData(
      { queryKey: parsnipKeys.analyses() },
      (oldData: any) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page: MyAnalysesResponse) =>
            patchPage(page, videoId, thumbnailUrl)
          ),
        };
      }
    );
  };

  const removeAnalysisFromCache = (analysisId: string) => {
    queryClient.setQueriesData(
      { queryKey: parsnipKeys.analyses() },
      (oldData: any) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page: MyAnalysesResponse) => ({
            ...page,
            analyses: page.analyses.filter(item => item.id !== analysisId),
            total: page.total - 1,
          })),
        };
      }
    );
  };

  return { invalidateAnalyses, patchThumbnailInCache, removeAnalysisFromCache };
};

// ─── Delete mutation ──────────────────────────────────────────────────────────

export const useDeleteAnalysis = () => {
  const { removeAnalysisFromCache } = useParsnipQueryClient();
  return useMutation({
    mutationFn: (analysisId: string) => parsnipApi.deleteAnalysis(analysisId),
    onSuccess: (_data, analysisId) => {
      removeAnalysisFromCache(analysisId);
    },
  });
};

// ─── Users query ──────────────────────────────────────────────────────────────

export const useAnalysisUsers = (isAdmin: boolean) => {
  return useQuery({
    queryKey: parsnipKeys.users(),
    queryFn: () => parsnipApi.getAnalysisUsers(),
    enabled: isAdmin,
    staleTime: 1000 * 60 * 10,
    select: (data) => data.users,
  });
};
